import { useState, useMemo, useEffect, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useDocumentStore } from "@/hooks/documents/useDocumentStore";
import { folderService } from "@/domains/documents/services/folderService";
import { useProjects } from "@/domains/documents/hooks/useProjects";
import { useAuth } from "@/hooks/useAuth";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { usePermissions } from "@/hooks/usePermissions";
import type { TreeFolder } from "@/domains/documents/types";
import type { BuildingProject } from "@/types/projects";
import { FolderIcon as ProjectFolderIcon } from "@/domains/documents/components/atoms/FolderIcon";
import { FolderIcon, FolderPlus, ChevronRight, Plus } from "lucide-react";
import { FileTypeIcon } from "@/components/ui/documents/file-type-icon";
import { Skeleton } from "@/components/ui/skeleton";
import { InlineCreate } from "./InlineCreate";
import type { FolderPickerDialogProps, FolderPickerDocument } from "./folderPickerTypes";
import { PickerFolderTree } from "@/components/documents/PickerFolderTree";
import { findFolderInTree, folderIdTrail } from "@/components/documents/pickerTreeUtils";
import { getFolderCreationContext } from "./folderCreationContext";
import { ProjectStatusBadge } from "@/domains/projects/status/ProjectStatusBadge";
import { ProjectSelectContent } from "@/domains/projects/status/ProjectSelectContent";
import { useProjectStatusFilter } from "@/domains/projects/status/useProjectStatusFilter";
import { cn } from "@/lib/utils";

export type { PickedFolder } from "./folderPickerTypes";

/**
 * Folder-only variant of `DocumentPicker`. Same two-panel UX (project
 * picker → folder tree on the left, contents on the right) but the
 * submit returns the selected folder instead of selected files. Used by
 * the chatbot files panel's "Upload to project" action, which then calls
 * `create_document_with_versioning` with `p_copy_from_document_id` to
 * promote a hidden-project chat attachment into a real project folder.
 *
 * Documents in the right panel are shown read-only — they're context to
 * help the user decide where to file, not picker targets.
 */
export function FolderPickerDialog({
  open,
  onOpenChange,
  buildingId,
  filename,
  onSelect,
  title,
  description,
  submitLabel,
  initialProjectId,
  initialFolderId = null,
  lockProject = false,
}: FolderPickerDialogProps) {
  const { t } = useLanguage();
  const copy = t.documents.destinationPicker;
  const documentStore = useDocumentStore();
  const queryClient = useQueryClient();
  const { userProfile } = useAuth();
  const { currentOrganization } = useOrganizationContext();
  const { toast } = useToast();
  // What the user picked, remembered against the destination the CALLER asked
  // for when they picked it — see the derivation below.
  const [picked, setPicked] = useState<{
    against?: string;
    projectId: string | null;
    folderId: string | null;
  }>({ projectId: null, folderId: null });
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [createInFlight, setCreateInFlight] = useState(false);

  const { projects, createProject } = useProjects(buildingId);
  const { canWriteFilesInProject } = usePermissions();
  // Every caller files something INTO the picked folder, so only projects the
  // user may add to are offered — a member's per-project permissions can rule
  // some out, and picking one would end in a refused write.
  const visibleProjects = useMemo(
    () =>
      projects.filter(
        (p) =>
          (!("hidden" in p) || !(p as { hidden?: boolean }).hidden) &&
          canWriteFilesInProject(Number(p.id))
      ),
    [projects, canWriteFilesInProject]
  );

  // Only a project the user can see counts as the caller's ask; anything else
  // falls through to the auto-selection below.
  const callerProjectId = useMemo(
    () =>
      initialProjectId && visibleProjects.some((p) => p.id === initialProjectId)
        ? initialProjectId
        : null,
    [initialProjectId, visibleProjects]
  );

  // The pick has to outlive an opening — the dialog stays mounted
  // (ChatbotFilesPanel keeps one for every file it promotes) and has always
  // reopened where it left off — but a caller naming a destination names it on
  // EVERY opening, not just the first. Deriving the selection rather than
  // resetting it on the way in is what lets both hold: a changed destination
  // (project or folder) supersedes a pick made against the previous one, and a
  // caller that names none never disturbs it.
  const callerAsk = initialProjectId ? `${initialProjectId}/${initialFolderId ?? ""}` : undefined;
  const pickIsCurrent = picked.against === callerAsk;
  const selectedProjectId = pickIsCurrent ? picked.projectId : callerProjectId;
  const selectedFolderId = pickIsCurrent
    ? picked.folderId
    : callerProjectId
      ? initialFolderId
      : null;
  const statusFilter = useProjectStatusFilter(visibleProjects, selectedProjectId);
  // Functional updates, so a handler that changes both (picking a project
  // clears the folder) cannot lose the first of the two.
  const setSelectedProjectId = useCallback(
    (projectId: string | null) =>
      setPicked((prev) => ({
        against: callerAsk,
        projectId,
        folderId: prev.against === callerAsk ? prev.folderId : null,
      })),
    [callerAsk]
  );
  const setSelectedFolderId = useCallback(
    (folderId: string | null) =>
      setPicked((prev) => ({
        against: callerAsk,
        projectId: prev.against === callerAsk ? prev.projectId : callerProjectId,
        folderId,
      })),
    [callerAsk, callerProjectId]
  );

  useEffect(() => {
    // A locked picker never swaps in some other project for the caller's.
    if (!open || lockProject || visibleProjects.length === 0 || selectedProjectId !== null) return;
    if (visibleProjects.length === 1) {
      setSelectedProjectId(visibleProjects[0].id);
    } else {
      const def = visibleProjects.find((p) => p.is_default);
      if (def) setSelectedProjectId(def.id);
    }
  }, [open, lockProject, visibleProjects, selectedProjectId, setSelectedProjectId]);

  // Drop the cached contents on the way in, driven by the controlled `open`
  // prop rather than the Radix callback: Radix does not emit
  // `onOpenChange(true)` for a dialog a parent opens, so anything hung off that
  // callback never runs on a reopen. The query is keyed by folder + project
  // only, so without this a reopen on the same folder is served the previous
  // opening's array synchronously and only then refetches — showing the
  // destination folder as it was before the last upload landed, which is how a
  // user comes to file a duplicate. Cache only: the selection is derived above
  // and the inline create forms keep their own state one level down, so
  // neither needs resetting here.
  useEffect(() => {
    if (!open) return;
    queryClient.removeQueries({ queryKey: ["folder-picker-docs"] });
  }, [open, queryClient]);

  const handleCreateProject = (name: string) =>
    // Resolves once the create has settled, so the form knows whether to
    // collapse — `createProject` is the react-query `mutate` (fire-and-forget),
    // whose onSuccess handles the toast + ["building-projects"] invalidation.
    // We only capture the new id so the dialog selects it.
    new Promise<boolean>((resolve) => {
      setCreateInFlight(true);
      createProject(
        { building_id: buildingId, name, description: undefined },
        {
          onSuccess: (newProject: BuildingProject | null) => {
            if (newProject?.id) {
              setSelectedProjectId(String(newProject.id));
              setSelectedFolderId(null);
            }
            setCreateInFlight(false);
            resolve(true);
          },
          onError: () => {
            setCreateInFlight(false);
            resolve(false);
          },
        }
      );
    });

  const handleCreateFolder = async (name: string): Promise<boolean> => {
    const context = getFolderCreationContext({
      projectId: selectedProjectId,
      userId: userProfile?.id,
      organizationId: currentOrganization?.id,
      toast,
    });
    if (!context) return false;

    setCreateInFlight(true);
    try {
      const created = await folderService.createCustomFolder(
        buildingId,
        name,
        // Category is a legacy folder-config field — chatbot saves don't
        // distinguish documents vs drawings, so default to "documents".
        "documents",
        context.userId,
        context.organizationId,
        selectedFolderId ?? undefined,
        context.projectId
      );
      // Refetch the folder tree + the right-panel child queries so the
      // new folder appears + can be selected.
      await queryClient.invalidateQueries({ queryKey: ["folder-picker-folders", buildingId] });
      await queryClient.invalidateQueries({
        queryKey: ["folder-picker-docs", buildingId, selectedFolderId, context.projectId],
      });
      setSelectedFolderId(String(created.id));
      expandFolder(selectedFolderId);
      toast({ title: copy.folderCreated, description: name });
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast({ variant: "destructive", title: copy.folderCreateFailed, description: message });
      return false;
    } finally {
      setCreateInFlight(false);
    }
  };

  const { data: folderTree = [] } = useQuery({
    queryKey: ["folder-picker-folders", buildingId],
    queryFn: async () => {
      const folders = await folderService.getFolders(buildingId);
      return folderService.buildFolderTree(folders);
    },
    enabled: open && !!buildingId,
  });

  const projectFolderTree = useMemo(() => {
    if (!selectedProjectId) return [];
    const pid = Number(selectedProjectId);
    return folderTree.filter((f) => f.project_id === pid);
  }, [folderTree, selectedProjectId]);

  // Opened on a folder the caller named: unfold the tree down to it, so the
  // selection is visible rather than buried under collapsed ancestors.
  useEffect(() => {
    if (!open || !initialFolderId || selectedFolderId !== initialFolderId) return;
    const trail = folderIdTrail(projectFolderTree, initialFolderId);
    if (trail.length === 0) return;
    setExpandedFolders((previous) => {
      if (trail.every((id) => previous.has(id))) return previous;
      const next = new Set(previous);
      for (const id of trail) next.add(id);
      return next;
    });
  }, [open, initialFolderId, selectedFolderId, projectFolderTree]);

  const childFolders = useMemo(() => {
    if (!selectedProjectId) return [];
    if (!selectedFolderId) return projectFolderTree;
    const folder = findFolderInTree(projectFolderTree, selectedFolderId);
    return folder?.children ?? [];
  }, [selectedFolderId, projectFolderTree, selectedProjectId]);

  const documentsEnabled = open && !!buildingId && !!selectedProjectId;
  const {
    data: documents = [],
    isError: documentsFailed,
    isPending: documentsPending,
    refetch: refetchDocuments,
  } = useQuery<FolderPickerDocument[]>({
    queryKey: ["folder-picker-docs", buildingId, selectedFolderId, selectedProjectId],
    queryFn: async () => {
      // `enabled` above already gates on a truthy selectedProjectId; this guard
      // re-establishes that for the type checker without a non-null assertion.
      if (!selectedProjectId) return [];
      // Fresh, rather than invalidating the project on open: invalidation
      // DELETES every slot, emptying the cache under whatever documents
      // surface is open behind this dialog (and sending its in-flight loads
      // back to the network), while a fresh peek writes this one slot back.
      const docs = await documentStore.peekFolderDocuments(selectedProjectId, selectedFolderId, {
        fresh: true,
      });
      return (docs ?? []).map((d: Record<string, unknown>) => ({
        id: d.id as number,
        filename: d.filename as string,
        content_type: (d.content_type as string) ?? null,
        folder_id: (d.folder_id as number) ?? null,
      }));
    },
    enabled: documentsEnabled,
    staleTime: 0,
  });

  const projectName =
    visibleProjects.find((p) => p.id === selectedProjectId)?.name ?? copy.projectFallback;

  const folderPathMap = useMemo(() => {
    const map = new Map<string, string>();
    function walk(folders: TreeFolder[], parentPath: string) {
      for (const folder of folders) {
        const current = parentPath ? `${parentPath}/${folder.name}` : `/${folder.name}`;
        map.set(folder.id, current);
        if (folder.children.length > 0) walk(folder.children, current);
      }
    }
    walk(projectFolderTree, "");
    return map;
  }, [projectFolderTree]);

  const breadcrumb = useMemo(() => {
    const root = { id: null as string | null, name: projectName };
    if (!selectedFolderId) return [root];
    const path: Array<{ id: string | null; name: string }> = [root];
    function findPath(folders: TreeFolder[], targetId: string): boolean {
      for (const folder of folders) {
        if (folder.id === targetId) {
          path.push({ id: folder.id, name: folder.name });
          return true;
        }
        if (folder.children.length > 0) {
          path.push({ id: folder.id, name: folder.name });
          if (findPath(folder.children, targetId)) return true;
          path.pop();
        }
      }
      return false;
    }
    findPath(projectFolderTree, selectedFolderId);
    return path;
  }, [selectedFolderId, projectFolderTree, projectName]);

  const toggleExpand = (folderId: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) next.delete(folderId);
      else next.add(folderId);
      return next;
    });
  };

  const expandFolder = (folderId: string | null) => {
    if (!folderId) return;
    setExpandedFolders((previous) => new Set(previous).add(folderId));
  };

  const navigateToFolder = (folderId: string) => {
    setSelectedFolderId(folderId);
    toggleExpand(folderId);
  };

  // `null` in two different states: no folder selected (project root), and a
  // folder selected whose path isn't in `folderPathMap` yet (just created,
  // tree not refetched). Callers pair it with `selectedFolderId` to tell them
  // apart — the labels below do, so "path unknown" never reads as root.
  const selectedFolderPath = selectedFolderId
    ? (folderPathMap.get(selectedFolderId) ?? null)
    : null;

  const handleSubmit = () => {
    if (!selectedProjectId) return;
    onSelect({
      projectId: selectedProjectId,
      projectName,
      folderId: selectedFolderId,
      folderPath: selectedFolderPath,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* `pointer-events-auto` and a z above the default 50, so this works when
          opened from inside another modal dialog: a modal Radix dialog sets
          `pointer-events: none` on the body, and this portals into that body.
          The template form keeps its dialog open while asking where to file, so
          without these the destination picker renders and ignores every click.
          Both are no-ops when it is the only dialog on screen. */}
      <DialogContent className="pointer-events-auto z-[60] flex h-[640px] max-h-[85vh] max-w-3xl flex-col">
        <DialogHeader>
          <DialogTitle>{title ?? copy.title}</DialogTitle>
          <DialogDescription>
            {description ??
              (filename
                ? copy.descriptionWithFile.replace("{filename}", filename)
                : copy.description)}
          </DialogDescription>
        </DialogHeader>

        {lockProject ? (
          // The caller's page is already scoped to one project, so the project
          // is context here, not a choice.
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {copy.projectLabel}
            </span>
            <span className="flex min-w-0 items-center gap-2 text-sm font-medium">
              <ProjectFolderIcon isOpen={false} />
              <span className="truncate">{projectName}</span>
            </span>
          </div>
        ) : (
          <div className="flex flex-col gap-1.5">
            <InlineCreate
              label={copy.newProject}
              placeholder={copy.projectNamePlaceholder}
              icon={<Plus className="h-3.5 w-3.5" />}
              busy={createInFlight}
              classes={{
                trigger: "h-9 gap-1 text-xs",
                form: "flex items-center gap-2 pl-[58px]",
                input: "h-9 max-w-sm flex-1 text-sm",
              }}
              onCreate={handleCreateProject}
            >
              {(expanded, trigger) => (
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {copy.projectLabel}
                  </span>
                  <Select
                    value={selectedProjectId ?? ""}
                    onValueChange={(val) => {
                      setSelectedProjectId(val);
                      setSelectedFolderId(null);
                    }}
                    disabled={expanded}
                  >
                    <SelectTrigger className="h-9 max-w-sm flex-1 text-sm">
                      <SelectValue placeholder={copy.selectProject} />
                    </SelectTrigger>
                    {/* z-[70]: portals to the body at the default z-50, which would
                      paint under this dialog panel's z-[60]. */}
                    <ProjectSelectContent statusFilter={statusFilter} className="z-[70]">
                      {/* Every project stays mounted — see isShown. */}
                      {statusFilter.ordered.map((p) => (
                        <SelectItem
                          key={p.id}
                          value={p.id}
                          disabled={!statusFilter.isShown(p)}
                          className={cn(!statusFilter.isShown(p) && "hidden")}
                        >
                          <div className="flex items-center gap-2">
                            <ProjectFolderIcon isOpen={false} />
                            <ProjectStatusBadge variant="dot" status={p.status} />
                            <span>{p.name}</span>
                            {p.is_default && (
                              <span className="text-[10px] text-muted-foreground">
                                {copy.defaultProject}
                              </span>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </ProjectSelectContent>
                  </Select>
                  {trigger}
                </div>
              )}
            </InlineCreate>
          </div>
        )}

        <div className="flex items-center gap-1 text-sm text-muted-foreground">
          {breadcrumb.map((crumb, i) => (
            <span key={crumb.id ?? "root"} className="flex items-center gap-1">
              {i > 0 && <ChevronRight className="h-3 w-3" />}
              <button
                className="hover:text-foreground hover:underline"
                onClick={() => setSelectedFolderId(crumb.id)}
              >
                {crumb.name}
              </button>
            </span>
          ))}
        </div>

        {/* WHA-3393: `min-h-0` lets the flex child shrink below its content
            so the footer stays reachable when the viewport is short (e.g.
            when the browser is zoomed in). */}
        <div className="flex min-h-0 flex-1 gap-3 overflow-hidden rounded-md border">
          <div className="w-[220px] shrink-0 overflow-y-auto border-r bg-muted/30 py-2">
            {selectedProjectId && projectFolderTree.length > 0 && (
              <PickerFolderTree
                folders={projectFolderTree}
                selectedFolderId={selectedFolderId}
                expandedFolderIds={expandedFolders}
                onSelect={setSelectedFolderId}
                onToggle={toggleExpand}
              />
            )}
            {selectedProjectId && projectFolderTree.length === 0 && (
              <p className="px-3 py-2 text-xs text-muted-foreground">{copy.noFolders}</p>
            )}
          </div>

          <div className="flex flex-1 flex-col overflow-hidden py-2 pr-2">
            {!selectedProjectId ? (
              <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                {copy.selectProjectToBrowse}
              </div>
            ) : (
              <>
                {/* New-folder affordance: creates a child of the currently
                 *  selected folder (or under the project root when no
                 *  folder is selected). Position above the contents list
                 *  so it stays reachable when the folder is empty. */}
                <InlineCreate
                  label={copy.newFolderHere}
                  placeholder={copy.folderNamePlaceholder}
                  icon={<FolderPlus className="h-3.5 w-3.5" />}
                  busy={createInFlight}
                  classes={{
                    trigger: "h-7 shrink-0 gap-1 text-xs",
                    form: "mb-2 flex items-center gap-2",
                    input: "h-9 flex-1 text-sm",
                  }}
                  onCreate={handleCreateFolder}
                >
                  {(_expanded, trigger) => (
                    <div className="mb-2 flex items-center justify-between gap-2 border-b pb-2">
                      <span className="truncate text-xs text-muted-foreground">
                        {selectedFolderId
                          ? selectedFolderPath
                            ? copy.inFolder.replace("{path}", selectedFolderPath)
                            : copy.inSelectedFolder
                          : copy.inProjectRoot.replace("{project}", projectName)}
                      </span>
                      {trigger}
                    </div>
                  )}
                </InlineCreate>
                <div className="flex-1 overflow-y-auto">
                  {documentsFailed ? (
                    // Never "empty folder" over a failed peek: the contents are
                    // here to stop the user filing a duplicate, and an empty
                    // list is exactly the answer that invites one.
                    <div className="flex h-full flex-col items-center justify-center gap-2 px-4 text-center text-sm text-destructive">
                      {copy.contentsLoadFailed}
                      <Button variant="outline" size="sm" onClick={() => void refetchDocuments()}>
                        {copy.tryAgain}
                      </Button>
                    </div>
                  ) : documentsEnabled && documentsPending ? (
                    // Nor over a peek still in flight, for the same reason: the
                    // reset on open drops the cached array, so every opening
                    // starts with nothing to show and "empty folder" would be
                    // the answer for the whole round-trip — invitation to file
                    // the duplicate that is already there.
                    <div
                      className="space-y-2 px-2 py-1"
                      role="status"
                      aria-label={copy.loadingContents}
                    >
                      {[0, 1, 2, 3].map((i) => (
                        <div key={i} className="flex items-center gap-2">
                          <Skeleton className="h-4 w-4 shrink-0 rounded" />
                          <Skeleton className="h-4 flex-1" />
                        </div>
                      ))}
                    </div>
                  ) : childFolders.length === 0 && documents.length === 0 ? (
                    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                      {copy.emptyFolder}
                    </div>
                  ) : (
                    <div className="space-y-0.5">
                      {childFolders.map((folder) => (
                        <div
                          key={`folder-${folder.id}`}
                          className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-muted/50"
                          onClick={() => navigateToFolder(folder.id)}
                        >
                          <FolderIcon className="h-4 w-4 shrink-0 text-primary" />
                          <span className="truncate text-sm font-medium">{folder.name}</span>
                        </div>
                      ))}
                      {documents.map((doc) => (
                        <div
                          key={`doc-${doc.id}`}
                          className="flex items-center gap-2 rounded px-2 py-1.5 opacity-70"
                          title={copy.existingFile}
                        >
                          <FileTypeIcon filename={doc.filename} />
                          <span className="block truncate text-sm">{doc.filename}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        <DialogFooter className="items-center sm:justify-between">
          <span className="text-xs text-muted-foreground">
            {selectedProjectId ? (
              <>
                {copy.destination}{" "}
                <span className="font-medium text-foreground">{projectName}</span>
                {/* `folderPathMap` values mirror the DB `folder_path` shape and
                 *  already carry their own leading slash (`/Plans/Floor 1`), so
                 *  the separator here is a plain space — same as the toast in
                 *  ChatbotFilesPanel. A `" / "` would render `Project / /Plans`.
                 *  The path-unknown fallback brings its own dash separator for
                 *  the same reason, and never collapses to the project alone —
                 *  that is what the project root shows. */}
                {selectedFolderId && (
                  <>
                    {" "}
                    <span className="font-medium text-foreground">
                      {selectedFolderPath ?? copy.selectedFolderFallback}
                    </span>
                  </>
                )}
              </>
            ) : (
              copy.pickProjectToEnable
            )}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {copy.cancel}
            </Button>
            <Button onClick={handleSubmit} disabled={!selectedProjectId}>
              {submitLabel ?? copy.submit}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
